import React from 'react';
import { Input, InputNumber, Select } from 'antd';
import YearInput from '../components/shared/YearInput';
import DateInput from '../components/shared/DateInput';
import DateRangeInput from '../components/shared/DateRangeInput';

/**
 * 根据字段类型渲染对应的编辑输入组件
 *
 * @param {Object} field       - 字段定义（含 type, key, label, options, maxLength）
 * @param {Object} record      - 当前行数据
 * @param {Object} deps        - 组件上下文依赖
 * @param {Function} deps.handleCellChange  - 单元格值变更回调 (rowKey, fieldKey, value, immediate)
 * @param {Function} deps.stopEditing       - 停止编辑状态
 * @param {Function} deps.handleTabNavigation - Tab 导航 (record, fieldKey, shiftKey)
 * @param {string}  deps.editingCell        - 当前正在编辑的单元格 key
 * @param {boolean} deps.hasChanges         - 是否有未保存变更
 */
export function renderInputComponent(field, record, {
  handleCellChange,
  stopEditing,
  handleTabNavigation,
  editingCell,
  hasChanges,
}) {
  const value = record[field.key];
  const cellKey = `${record.key}-${field.key}`;

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      stopEditing();
      setTimeout(() => {
        handleTabNavigation(record, field.key, e.shiftKey);
      }, 50);
    }
  };

  const handleBlur = (e) => {
    if (!e) {
      stopEditing();
      return;
    }

    const relatedTarget = e.relatedTarget || document.activeElement;
    const isClickingEditableCell = relatedTarget && (
      relatedTarget.closest('.ant-table-cell') ||
      relatedTarget.closest('input') ||
      relatedTarget.closest('textarea') ||
      relatedTarget.closest('.ant-select')
    );

    if (!isClickingEditableCell) {
      setTimeout(() => {
        const currentTarget = e.target || document.activeElement;
        if (document.activeElement !== currentTarget && !document.activeElement.closest('.ant-table-cell')) {
          stopEditing();
          if (hasChanges) {
            handleCellChange(record.key, field.key, value, true);
          }
        }
      }, 150);
    }
  };

  switch (field.type) {
    case 'number': {
      const isYear = field.key === 'year' || field.key === '年份' || field.label.includes('年份');
      const isMoney = field.label.includes('万元') || field.label.includes('经费');

      if (isYear) {
        return (
          <YearInput
            value={value ? String(value) : ''}
            onChange={(e) => {
              handleCellChange(record.key, field.key, e.target.value ? parseInt(e.target.value) : undefined, false);
            }}
            onBlur={handleBlur}
            onPressEnter={handleBlur}
            onKeyDown={handleKeyDown}
            style={{ width: '100%' }}
            placeholder={`请输入${field.label}（1900-2100）`}
            data-cell-key={cellKey}
            autoFocus={editingCell === cellKey}
          />
        );
      }

      return (
        <InputNumber
          value={value}
          onChange={(val) => handleCellChange(record.key, field.key, val)}
          onBlur={handleBlur}
          onPressEnter={handleBlur}
          onKeyDown={handleKeyDown}
          style={{ width: '100%' }}
          min={0}
          precision={isMoney ? 2 : 0}
          placeholder={`请输入${field.label}`}
          data-cell-key={cellKey}
          autoFocus={editingCell === cellKey}
        />
      );
    }

    case 'date':
      return (
        <DateInput
          value={value ? (typeof value === 'string' ? value : String(value)) : ''}
          onChange={(e) => {
            handleCellChange(record.key, field.key, e.target.value, false);
          }}
          onBlur={handleBlur}
          onPressEnter={handleBlur}
          onKeyDown={handleKeyDown}
          style={{ width: '100%' }}
          placeholder="YYYY-MM-DD"
          autoFocus={editingCell === cellKey}
        />
      );

    case 'daterange': {
      const rangeValue = value && Array.isArray(value) ? value : ['', ''];
      return (
        <DateRangeInput
          value={rangeValue}
          onChange={(e) => {
            const newValue = Array.isArray(e.target.value) ? e.target.value : (e.target.value ? [e.target.value, ''] : ['', '']);
            handleCellChange(record.key, field.key, newValue, false);
          }}
          onBlur={handleBlur}
          onPressEnter={handleBlur}
          onKeyDown={handleKeyDown}
          style={{ width: '100%' }}
          placeholder="YYYY-MM-DD ~ YYYY-MM-DD"
          autoFocus={editingCell === cellKey}
        />
      );
    }

    case 'select':
      return (
        <Select
          value={value}
          onChange={(val) => {
            handleCellChange(record.key, field.key, val, true);
            setTimeout(() => {
              stopEditing();
            }, 100);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{ width: '100%' }}
          placeholder={`请选择${field.label}`}
          showSearch={field.options && field.options.length > 5}
          autoFocus={editingCell === cellKey}
        >
          {field.options?.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );

    case 'textarea':
      return (
        <Input.TextArea
          value={value}
          onChange={(e) => handleCellChange(record.key, field.key, e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          rows={2}
          placeholder={`请输入${field.label}`}
          maxLength={field.maxLength}
          autoFocus={editingCell === cellKey}
        />
      );

    default:
      return (
        <Input
          value={value}
          onChange={(e) => handleCellChange(record.key, field.key, e.target.value)}
          onBlur={handleBlur}
          onPressEnter={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={`请输入${field.label}`}
          maxLength={field.maxLength}
          autoFocus={editingCell === cellKey}
        />
      );
  }
}
